import type { QualityGateResponse } from '@/services/copilot';
import { useTranslation } from 'react-i18next';

import { useBrand } from '@/store/useBrand';
import { sanitizarHtml } from '@/utils/sanitize';

import styles from './LivePublishedPreview.module.css';

interface Props {
  title: string;
  excerpt?: string;
  lead?: string;
  bodyHtml: string;
  imageUrl?: string | null;
  qualityGates?: QualityGateResponse | null;
  generationMeta?: {
    modo: string;
    modelo: string | null;
    tokens_in: number;
    tokens_out: number;
    custo_usd: number;
    duracao_ms: number;
  } | null;
  siteName?: string;
  category?: string;
}

export function LivePublishedPreview({
  title,
  excerpt,
  lead,
  bodyHtml,
  imageUrl,
  qualityGates,
  generationMeta,
  siteName,
  category = 'NOTÍCIAS',
}: Props) {
  const { t } = useTranslation([
    'common',
    'nav',
    'board',
    'articles',
    'analytics',
    'feeddrive',
    'config',
    'strategist',
  ]);
  void t;
  // A prévia existe para o repórter ver como a matéria vai sair NO SITE DELE.
  // O default era o literal 'WIREDESK PORTAL' — um portal que não existe, com
  // o nome do fornecedor. Agora vem da marca do próprio tenant (a mesma que
  // pinta o cabeçalho), e só cai num rótulo neutro se ninguém a configurou.
  const marca = useBrand((s) => s.brand);
  const nomeDoSite = siteName ?? marca.name ?? 'SEU PORTAL';
  const charCount = title ? title.length : 0;
  const charClass =
    charCount >= 50 && charCount <= 70
      ? styles.charOk
      : charCount > 0
        ? styles.charWarn
        : styles.charMuted;

  return (
    <div className={styles.container} aria-label="Prévia viva de publicação">
      <div className={styles.headerBar}>
        <span className={styles.badgePortal}>{nomeDoSite}</span>
        <span className={styles.badgeCategory}>{category}</span>
        <span className={`${styles.badgeChar} ${charClass}`}>{charCount} / 70 chars</span>
      </div>

      <article className={styles.articleBody}>
        {title ? (
          <h1 className={styles.headline}>{title}</h1>
        ) : (
          <h1 className={styles.headlinePlaceholder}>
            Manchete Principal da Notícia (50 a 70 caracteres)
          </h1>
        )}

        {excerpt && <p className={styles.excerpt}>{excerpt}</p>}

        {imageUrl ? (
          <div className={styles.imageWrapper}>
            <img src={imageUrl} alt={title || 'Capa do artigo'} className={styles.coverImage} />
            <span className={styles.imageBadge}>Discover ≥ 1200px WebP OK</span>
          </div>
        ) : (
          <div className={styles.imagePlaceholder}>
            <span>🖼️ Imagem de Capa Elegível ao Discover (1200px × 675px)</span>
          </div>
        )}

        {lead && <p className={styles.leadText}>{lead}</p>}

        {bodyHtml ? (
          <div
            className={styles.htmlContent}
            dangerouslySetInnerHTML={{ __html: sanitizarHtml(bodyHtml) }}
          />
        ) : (
          <div className={styles.bodyPlaceholder}>
            <p>
              O texto formatado, subcabeçalhos H2/H3, citações inline e tabelas GEO aparecerão aqui
              em tempo real.
            </p>
          </div>
        )}
      </article>

      {qualityGates && (
        <div className={styles.footerGates}>
          <div className={styles.gatePill}>
            <span>1º Rastreio SSR:</span>{' '}
            <strong>{qualityGates.first_crawl_readiness_score}%</strong>
          </div>
          <div className={styles.gatePill}>
            <span>Sem Clickbait:</span>{' '}
            <strong>{((1 - qualityGates.discover_clickbait_risk) * 100).toFixed(0)}%</strong>
          </div>
          <div className={styles.gatePill}>
            <span>Largura Capa:</span> <strong>{qualityGates.featured_image_min_width}px</strong>
          </div>
          {generationMeta && (
            <div
              className={styles.gatePill}
              style={{
                display: 'flex',
                gap: '8px',
                fontSize: '11px',
                opacity: 0.85,
                marginTop: '4px',
              }}
            >
              <span>
                {generationMeta.modo === 'ia' ? generationMeta.modelo || 'IA' : 'Formatador'}
              </span>
              <span>
                Tokens: {generationMeta.tokens_in}/{generationMeta.tokens_out}
              </span>
              <span>{generationMeta.duracao_ms}ms</span>
              <span>US$ {generationMeta.custo_usd.toFixed(4)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
